import React, { useEffect, useRef, useState } from "react"
import { Alert, Button, Modal, Space, Spin, Switch, Tag, TimePicker, Typography, message } from "antd"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { request } from "../../../../utils/config"

// ============================================================================
// tog'a #2504 (2026-09-24): «📊 Ежедневный отчёт» — har xodim o'zi boshqaradi: yoqish/o'chirish, vaqt (standart 20:00),
// Telegram'ga ulash (mijozlar boti, bir martalik havola), bugungi xulosani ko'rish va hozir yuborish.
// Xulosa har kuni tizim chatidagi «📊 Kunlik hisobot» suhbatiga (+ 🔔) va ulangan bo'lsa Telegram'ga keladi.
// ============================================================================
type Settings = { available: boolean; enabled: boolean; time: string; telegramLinked: boolean; lastSentDate?: string | null }

export default function DigestSettings() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data, isFetching, refetch } = useQuery<Settings>(["DIGEST_SETTINGS"], async () => (await request({ url: "Digest/Settings" })).data)
  const [busy, setBusy] = useState<string | null>(null)
  const [preview, setPreview] = useState<any>(null)
  const [waitLink, setWaitLink] = useState(false)
  const timer = useRef<any>(null)

  // «Telegram'ni ulash» bosilgach — bot «Start» dan keyin ulanish holati o'zi yangilanadi (2 daqiqagacha)
  useEffect(() => {
    if (!waitLink) return
    let n = 0
    timer.current = setInterval(async () => {
      n++
      const r = await refetch()
      if (r.data?.telegramLinked || n >= 40) {
        clearInterval(timer.current)
        setWaitLink(false)
        if (r.data?.telegramLinked) message.success(t("digestLinkedOk", "Telegram подключён — отчёт будет приходить и туда"))
      }
    }, 3000)
    return () => clearInterval(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitLink])

  if (!data) return <Spin spinning={isFetching} />
  if (!data.available) return <Alert type="info" showIcon message={t("digestNotStaff", "Ежедневный отчёт доступен только сотрудникам")} />

  const save = async (patch: any, key: string) => {
    setBusy(key)
    try {
      const r = await request({ url: "Digest/Settings", method: "PUT", data: patch })
      qc.setQueryData(["DIGEST_SETTINGS"], r.data)
      message.success(t("spSaved", "Сохранено"))
    } catch (e: any) {
      message.error(e?.response?.data?.message || t("error", "Ошибка"))
    } finally {
      setBusy(null)
    }
  }

  const link = async () => {
    setBusy("link")
    try {
      const r = await request({ url: "Digest/Link", method: "POST" })
      const url = r.data?.url
      if (url) {
        window.open(url, "_blank", "noopener")
        setWaitLink(true)
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || t("error", "Ошибка"))
    } finally {
      setBusy(null)
    }
  }

  const unlink = async () => {
    setBusy("unlink")
    try {
      const r = await request({ url: "Digest/Unlink", method: "POST" })
      qc.setQueryData(["DIGEST_SETTINGS"], r.data)
    } catch (e: any) {
      message.error(e?.response?.data?.message || t("error", "Ошибка"))
    } finally {
      setBusy(null)
    }
  }

  const showPreview = async () => {
    setBusy("preview")
    try {
      const r = await request({ url: "Digest/Preview" })
      setPreview(r.data)
    } catch (e: any) {
      message.error(e?.response?.data?.message || t("error", "Ошибка"))
    } finally {
      setBusy(null)
    }
  }

  const sendNow = async () => {
    setBusy("send")
    try {
      const r = await request({ url: "Digest/SendNow", method: "POST" })
      if (r.data?.sent) {
        message.success(r.data?.telegram ? t("digestSentTg", "Отчёт отправлен в чат «Kunlik hisobot» и в Telegram") : t("digestSent", "Отчёт отправлен в чат «Kunlik hisobot»"))
        qc.invalidateQueries(["GET_CONVERSATIONS"])
        qc.invalidateQueries(["ALERTS_FEED"])
      } else {
        message.info(r.data?.message || t("digestEmpty", "Сегодня по вашим разделам нечего сообщить"))
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || t("error", "Ошибка"))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div data-testid="digest-settings" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Typography.Text type="secondary">
        {t("digestHint", "Каждый день в выбранное время — сводка только по вашим разделам и складу: продажи, закупки, касса, заказы, перемещения, возвраты, доставки, просроченные долги, KPI. Приходит в чат «📊 Kunlik hisobot» и в Telegram (если подключён).")}
      </Typography.Text>
      <Space wrap align="center">
        <Switch data-testid="digest-enabled" checked={data.enabled} loading={busy === "enabled"} onChange={(v) => save({ enabled: v }, "enabled")} />
        <span>{data.enabled ? t("digestOn", "Включён") : t("digestOff", "Выключен")}</span>
        <span style={{ marginLeft: 12 }}>{t("digestTime", "Время")}:</span>
        <TimePicker
          data-testid="digest-time"
          format="HH:mm"
          minuteStep={5}
          allowClear={false}
          disabled={!data.enabled || busy === "time"}
          value={moment(data.time || "20:00", "HH:mm")}
          onChange={(v) => v && save({ digestTime: v.format("HH:mm") }, "time")}
        />
      </Space>
      <Space wrap align="center">
        <span>Telegram:</span>
        {data.telegramLinked ? (
          <>
            <Tag color="green" data-testid="digest-tg-linked">{t("digestLinked", "подключён")}</Tag>
            <Button size="small" data-testid="digest-unlink" loading={busy === "unlink"} onClick={unlink}>{t("digestUnlink", "Отключить")}</Button>
          </>
        ) : (
          <>
            <Tag data-testid="digest-tg-notlinked">{t("digestNotLinked", "не подключён")}</Tag>
            <Button size="small" type="primary" data-testid="digest-link" loading={busy === "link"} onClick={link}>{t("digestLink", "Подключить Telegram")}</Button>
          </>
        )}
      </Space>
      {waitLink && (
        <Alert type="info" showIcon data-testid="digest-link-wait"
          message={t("digestLinkWait", "В открывшемся Telegram нажмите «Start» (Запустить) — подключение появится здесь автоматически. Ссылка действует 30 минут.")} />
      )}
      <Space wrap>
        <Button data-testid="digest-preview" loading={busy === "preview"} onClick={showPreview}>{t("digestPreview", "Показать сегодняшний отчёт")}</Button>
        <Button data-testid="digest-send" loading={busy === "send"} onClick={sendNow}>{t("digestSendNow", "Отправить сейчас")}</Button>
      </Space>
      {data.lastSentDate && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t("digestLastSent", "Последняя отправка по расписанию")}: {moment(data.lastSentDate).format("DD.MM.YYYY")}
        </Typography.Text>
      )}
      <Modal
        visible={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        title={"📊 " + t("digestTitle", "Ежедневный отчёт")}
        destroyOnClose
      >
        <div data-testid="digest-preview-body">
          {preview?.empty ? (
            <Alert type="info" showIcon message={t("digestEmpty", "Сегодня по вашим разделам нечего сообщить")} />
          ) : (
            <DigestSections scope={preview?.scope} sections={preview?.sections || []} />
          )}
        </div>
      </Modal>
    </div>
  )
}

/** Xulosa bo'limlari (chat kartasi bilan bir ko'rinish) */
export function DigestSections({ scope, sections }: { scope?: string | null; sections: { key?: string; title: string; lines: string[] }[] }) {
  return (
    <div className="digest-sections">
      {scope && <div className="digest-scope">🏬 {scope}</div>}
      {sections.map((s, i) => (
        <div key={s.key || i} className="digest-section" style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 600 }}>{s.title}</div>
          <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
            {(s.lines || []).map((l, j) => <li key={j}>{l}</li>)}
          </ul>
        </div>
      ))}
    </div>
  )
}
